/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */
import React, { useEffect } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { ThemeProvider, createTheme, alpha, lighten, darken, emphasize, getContrastRatio } from '@mui/material/styles';
import SuccessSnackbar from './components/shared/snackBar';
import './scss/style.scss';
import Loading from './loading';
import AccessDenied from './screens/accessdenied';
import { fetchCurrentUser } from './store/action/userAction';
import {
   BRAND_PRIMARY, BRAND_PRIMARY_DARK, BRAND_SECONDARY,
   BRAND_ERROR, BRAND_WARNING, BRAND_SUCCESS,
   BRAND_BG_DEFAULT, BRAND_BG_PAPER,
} from './theme/brandColors';

const _baseTheme = createTheme({
   palette: {
      primary: { main: BRAND_PRIMARY, dark: BRAND_PRIMARY_DARK },
      secondary: { main: BRAND_SECONDARY },
      error: { main: BRAND_ERROR },
      warning: { main: BRAND_WARNING },
      success: { main: BRAND_SUCCESS },
      background: { default: BRAND_BG_DEFAULT, paper: BRAND_BG_PAPER },
   },
   typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 600, fontSize: '2.5rem' },
      h2: { fontWeight: 600, fontSize: '2rem' },
      h3: { fontWeight: 600, fontSize: '1.75rem' },
      h4: { fontWeight: 600, fontSize: '1.5rem' },
      h5: { fontWeight: 600, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      button: { fontWeight: 500, textTransform: 'none' },
   },
   shape: {
      borderRadius: 8,
   },
   components: {
      MuiButton: {
         styleOverrides: {
            root: {
               textTransform: 'none',
               fontWeight: 500,
            },
            contained: {
               boxShadow: 'none',
               '&:hover': {
                  boxShadow: 'none',
               },
            },
         },
      },
      MuiCard: {
         styleOverrides: {
            root: {
               boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            },
         },
      },
      MuiPaper: {
         styleOverrides: {
            root: {
               backgroundImage: 'none',
            },
         },
      },
      MuiChip: {
         styleOverrides: {
            root: {
               fontWeight: 500,
            },
         },
      },
      MuiTextField: {
         styleOverrides: {
            root: {
               '& .MuiOutlinedInput-root': {
                   '&:hover fieldset': {
                      borderColor: BRAND_PRIMARY,
                   },
               },
            },
         },
      },
      MuiAppBar: {
         styleOverrides: {
            root: {
               boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
            },
         },
      },
   },
});
Object.assign(_baseTheme, { alpha, lighten, darken, emphasize, getContrastRatio });

const Layout = React.lazy(() => import('./components/containers/Layout'));

function App() {
   const dispatch = useDispatch();
   const authError = useSelector(state => state.user.authError);
   const userDetails = useSelector(state => state.user.userDetails);

   useEffect(() => {
      dispatch(fetchCurrentUser());
   }, [dispatch]);

   if (authError) {
      return <AccessDenied message={authError.message} />;
   }

   if (!userDetails) {
      return <Loading />;
   }

   return (
      <EmotionThemeProvider theme={_baseTheme}>
         <ThemeProvider theme={_baseTheme}>
            <HashRouter>
               <React.Suspense fallback={<Loading />}>
                  <Routes>
                     <Route path="/*" element={<Layout />} />
                  </Routes>
                  <SuccessSnackbar />
               </React.Suspense>
            </HashRouter>
         </ThemeProvider>
      </EmotionThemeProvider>
   );
}

export default App;
